import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
	getProtectedImageUriFromString,
	getProtectedImageUriFromStringResized,
} from '../../utils/FileAccessUtils';
import { Button, Image, Popover } from 'antd';
import './ProtectedImageThumbnails.less';
import { downloadImages } from '../../utils/ImageUtils';
import Resizer from 'react-image-file-resizer';
import pLimit from 'p-limit';
import { useBatchedState } from '../../hooks/useBatchedState';

const limit = pLimit(50);

export const ProtectedImageThumbnails: React.FC<any> = React.forwardRef(
	(
		{
			photoStrings = [],
			role,
			imageWidth,
			imageHeight,
			resize = false,
			style = {},
			showDownloadAll = false,
			imageDownloadName,
			allResizeComplete,
			showNumberOfImages,
			...rest
		},
		ref
	) => {
		const hideAfterIndex = showNumberOfImages && showNumberOfImages - 1;
		const photoUriMapRef = useRef<{ [key: string]: any }>({});
		const [photoUriMap, queueUpdatePhotoUriMap] = useBatchedState<{ [key: string]: any }>({}, 50);
		const hasCalledComplete = useRef(false);

		const photoStringsKey = useMemo(() => photoStrings.join(','), [photoStrings]);

		//If either photostring or resize variable changes, entire component will rerender
		useEffect(() => {
			hasCalledComplete.current = false;
		}, [photoStringsKey, resize]);

		const queueUpdate = (photoString: string, uri: any) => {
			photoUriMapRef.current[photoString] = uri;
			queueUpdatePhotoUriMap({ [photoString]: uri });
		};

		useEffect(() => {
			if (!resize || !allResizeComplete) return;

			let interval: NodeJS.Timeout | null = null;

			const checkIfComplete = () => {
				const total = photoStrings.length;
				const keys = Object.keys(photoUriMapRef.current);
				const complete =
					total === keys.length &&
					photoStrings.every((key) => {
						const value = photoUriMapRef.current[key];
						return value && value !== 'pending';
					});

				if (complete && !hasCalledComplete.current) {
					hasCalledComplete.current = true;
					allResizeComplete();
					clearInterval(interval);
				}
			};

			interval = setInterval(checkIfComplete, 100);
			return () => {
				if (interval) {
					clearInterval(interval);
				}
			};
		}, [resize, photoStrings, allResizeComplete, photoStringsKey]);

		const resizeImageFromUrl = useCallback(
			async (photoString) => {
				const s3url = getProtectedImageUriFromString(role)(photoString);
				try {
					const response = await fetch(s3url);
					const blob = await response.blob();
					const file = new File([blob], 'image.jpg', { type: blob.type });

					Resizer.imageFileResizer(
						file,
						600, // maxWidth
						800, // maxHeight
						'JPEG', // compressFormat
						80, // quality
						0, // rotation
						(uri) => {
							queueUpdate(photoString, uri);
						},
						'base64'
					);
				} catch (err) {
					queueUpdate(
						photoString,
						getProtectedImageUriFromStringResized(role)(photoString, 600, 800)
					);
				}
			},
			[role]
		);

		useEffect(() => {
			if (resize) {
				photoStrings.forEach((photoString) => {
					// Use 'pending' as a clear and non-falsy sentinel.
					if (!(photoString in photoUriMapRef.current)) {
						photoUriMapRef.current[photoString] = 'pending';
						limit(() => resizeImageFromUrl(photoString));
					}
				});
			} else {
				const staticMap = photoStrings.reduce((acc, photoString) => {
					const uri = getProtectedImageUriFromString(role)(photoString);
					photoUriMapRef.current[photoString] = uri;
					acc[photoString] = uri;
					return acc;
				}, {} as { [key: string]: string });

				queueUpdatePhotoUriMap(staticMap);
			}
		}, [photoStrings, queueUpdatePhotoUriMap, resize, resizeImageFromUrl, role]);

		return (
			<div
				className="ProtectedImageThumbnails"
				style={{ display: 'flex', flexWrap: 'wrap', ...style }}
				{...rest}
			>
				<Image.PreviewGroup>
					{photoStrings.map((photoString, idx) => {
						const isLastVisible = idx === hideAfterIndex;
						const shouldHide = hideAfterIndex && idx > hideAfterIndex;

						const image = (
							<div
								key={photoString}
								style={{
									position: 'relative',
									marginRight: 8,
									marginBottom: 8,
									width: imageWidth,
									height: imageHeight,
									overflow: 'hidden',
									display: shouldHide ? 'none' : 'block',
								}}
							>
								<Image
									src={photoUriMap[photoString]}
									style={{
										width: imageWidth,
										height: imageHeight,
										objectFit: 'cover',
									}}
								/>
								{isLastVisible && photoStrings.length > hideAfterIndex + 1 && (
									<div
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											backgroundColor: 'rgba(0, 0, 0, 0.5)',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											color: '#fff',
											fontSize: 18,
											fontWeight: 'bold',
											pointerEvents: 'none',
										}}
									>
										+{photoStrings.length - (hideAfterIndex + 1)}
									</div>
								)}
							</div>
						);

						return image;
					})}
				</Image.PreviewGroup>
				{showDownloadAll ? (
					<Popover content={'Download Images'}>
						<Button
							type={'link'}
							onClick={() => downloadImages(photoStrings, role, imageDownloadName)}
						>
							<i style={{ fontSize: imageHeight / 2 }} className={`icons8-font icons8-download`} />
						</Button>
					</Popover>
				) : null}
			</div>
		);
	}
);
